import { Row, Column } from '@algbra/ui/components/Grid/Grid'
import Icon from '@algbra/ui/components/Icon/Icon'
import FreeText from '@algbra/ui/components/FreeText/FreeText'
import Heading from '@algbra/ui/components/Heading/Heading'
import {
  MEDIA_QUERY_MOBILE,
  useMediaQuery,
} from '@algbra/ui/hooks/useMediaQuery'
import { getIconByName } from '@algbra/ui/icons'
import { resolveAsAttachment, resolveAsImage } from '@algbra/ui/resolvers'
import classNames from 'classnames'
import { GatsbyImage } from 'gatsby-plugin-image'
import { GatsbyResolvedAsset } from '@algbra/ui/types/image'
import Permalink from '@algbra/ui/components/Permalink/Permalink'
import { mapIndexed } from 'rambdax'
import React, { useEffect, useState } from 'react'
import axios from 'axios'

import styles from './HomeBoxNew.module.scss'
import { Box } from '../../zones/HomeBoxes/HomeBoxes'

export default function ZoneHomeBoxNew(props: Box & { index: number }) {
  const {
    gridSize,
    backgroundImage,
    imageFirst,
    logoImages,
    title,
    textColor,
    description,
    buttonText,
    buttonLink,
    disclaimer,
    disclaimerIcon,
    backgroundColorHEX,
    image,
    index,
    squareStyle,
    showTopRate,
    badgeText,
    footerImage,
    footerImageSvg,
    footerDisclaimer,
  } = props
  const [topRate, setTopRate] = useState<{ rate?: number } | null>(null)
  const [rateFailed, setRateFailed] = useState(false)
  useEffect(() => {
    if (!showTopRate) {
      return
    }
    axios
      .get('/.netlify/functions/topRate')
      .then(response => {
        if (typeof response.data?.rate === 'number') {
          setTopRate(response.data)
        } else {
          setRateFailed(true)
        }
      })
      .catch(() => setRateFailed(true))
  }, [showTopRate])
  const liveRate =
    showTopRate && typeof topRate?.rate === 'number'
      ? topRate.rate.toFixed(2)
      : undefined
  const isRateLoading = showTopRate && !liveRate && !rateFailed
  // While the rate is in flight the sentence still renders in full, with a
  // shimmer standing in for the digits. On failure we fall back to the CMS
  // copy entirely (regulated financial promotion — never show a stale or
  // placeholder number as if it were real).
  const rateFragment = liveRate
    ? `${liveRate}% EPR*`
    : `<span class="${styles.rateShimmer}" aria-hidden="true"></span> EPR*`
  const displayTitle =
    liveRate || isRateLoading
      ? `Grow your money with up to ${rateFragment}`
      : title
  const displayDescription =
    liveRate || isRateLoading
      ? `Earn up to <strong>${rateFragment}</strong> on your reserves, while helping support sustainable development globally.`
      : description

  // SVGs have no childImageSharp, so they resolve via publicURL instead of
  // GatsbyImage. Falls back to the raw Strapi URL if the ext is not one
  // resolveAsAttachment whitelists.
  const footerSvgUrl =
    footerImageSvg &&
    (resolveAsAttachment(footerImageSvg) || footerImageSvg.url)

  const freeTextColor = textColor === 'white' ? 'white' : 'primary'
  const isMobile = useMediaQuery(MEDIA_QUERY_MOBILE)
  const BoxDetailLink = props => {
    return (
      <Permalink className={styles.linkText} to={props.linkUrl}>
        <FreeText
          className={classNames(
            [styles.boxDetailLink, props.className],
            [styles[props.theme], props.theme]
          )}
          color={props.linkColor}
        >
          <div
            className={classNames(styles.icon, [
              styles[props.theme],
              props.theme,
            ])}
          >
            <Icon
              icon={getIconByName('arrow-right')}
              size={24}
              className={styles.arrow}
            />
          </div>
          <div className={styles.linkText}>{props.linkText}</div>
        </FreeText>
      </Permalink>
    )
  }

  return (
    <Column
      size={12}
      sm={gridSize === 'full' ? 12 : 6}
      key={`box-${index}`}
      className={styles.column}
    >
      <div
        className={classNames(styles.box, styles[gridSize], {
          [styles.hasBackgroundImage]: backgroundImage,
          [styles.square]: squareStyle,
        })}
        style={{
          backgroundColor: backgroundColorHEX,
          backgroundImage:
            backgroundImage && `url(${backgroundImage.resolved.publicURL})`,
        }}
      >
        {!!badgeText && (
          <div className={styles.badge}>
            <span>{badgeText}</span>
          </div>
        )}
        <Row>
          {image && (
            <Column
              style={{
                alignItems: 'center',
              }}
              size={12}
              sm={gridSize === 'full' ? 6 : 12}
              className={styles.imgColumn}
            >
              <GatsbyImage
                image={resolveAsImage(image)}
                alt={title}
                className={styles.itemImage}
                loading="lazy"
              />
            </Column>
          )}
          <Column size={12} sm={gridSize === 'full' ? 6 : 12}>
            <div
              className={classNames(styles.textContainer, {
                [styles.alignBottom]: !!imageFirst,
                [styles.alignCenter]: !!image,
              })}
            >
              <div>
                {logoImages?.length > 0 && (
                  <ul className={styles.logoImagesList}>
                    {mapIndexed(
                      (logoImage: GatsbyResolvedAsset) => (
                        <li key={logoImage.id}>
                          <GatsbyImage
                            image={resolveAsImage(logoImage)}
                            alt={title}
                            className={styles.itemImage}
                            loading="lazy"
                          />
                        </li>
                      ),
                      logoImages
                    )}
                  </ul>
                )}
                {title && (
                  <Heading
                    type="lg"
                    className={styles.title}
                    theme={textColor === 'white' ? 'dark' : 'light'}
                    dangerouslySetInnerHTML={{ __html: displayTitle }}
                  />
                )}
                <FreeText
                  className={styles.description}
                  color={freeTextColor}
                  innerHTML={!!liveRate || isRateLoading}
                >
                  {displayDescription}
                </FreeText>
                {buttonText && buttonLink && (
                  <BoxDetailLink
                    linkColor={textColor || 'black'}
                    linkUrl={buttonLink}
                    linkText={buttonText}
                    theme={textColor}
                  />
                )}
              </div>

              {disclaimer && (
                <div
                  className={classNames(styles.disclaimerContainer, {
                    [styles.paddingTop]: imageFirst && !isMobile,
                  })}
                >
                  {disclaimerIcon && (
                    <div
                      className={styles.disclaimerIcon}
                      style={{
                        backgroundImage:
                          disclaimerIcon &&
                          `url(${disclaimerIcon.resolved.publicURL})`,
                      }}
                    ></div>
                  )}

                  <FreeText
                    className={classNames(styles.disclaimer, styles[textColor])}
                  >
                    {disclaimer}
                  </FreeText>
                </div>
              )}

              {(footerSvgUrl || footerImage || footerDisclaimer) && (
                <div className={styles.footer}>
                  {footerSvgUrl ? (
                    <img
                      src={footerSvgUrl}
                      alt={title}
                      className={styles.footerImage}
                      loading="lazy"
                    />
                  ) : (
                    footerImage && (
                      <GatsbyImage
                        image={resolveAsImage(footerImage)}
                        alt={title}
                        className={styles.footerImage}
                        loading="lazy"
                      />
                    )
                  )}
                  {footerDisclaimer && (
                    <div className={styles.footerDisclaimerContainer}>
                      <FreeText
                        className={classNames(
                          styles.footerDisclaimer,
                          styles[textColor]
                        )}
                      >
                        {footerDisclaimer}
                      </FreeText>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Column>
        </Row>
      </div>
    </Column>
  )
}
